/**
 * Линейка и измерение площади.
 *
 * Инструмент рисует во временный GeoJSON-источник, а не в тайлы: измерения
 * живут только в текущей сессии и не должны попадать ни в permalink, ни в
 * опубликованные данные.
 *
 * Считает turf по сфероиду, а не по экранным координатам, поэтому результат
 * не зависит от того, в какой части проекции Меркатора идёт измерение.
 */

import area from "@turf/area";
import length from "@turf/length";
import type { Feature, Polygon, LineString, Position } from "geojson";
import { GeoJSONSource, MapLibreMap } from "maplibre-gl";
import { formatArea, formatLength } from "~/ui/featureCard";

export type MeasureMode = "off" | "distance" | "area";

const SOURCE_ID = "measure-draw";
const LINE_LAYER = "measure-line";
const FILL_LAYER = "measure-fill";
const POINT_LAYER = "measure-points";

export interface MeasureOptions {
  map: MapLibreMap;
  onResult: (text: string | null) => void;
}

export class MeasureTool {
  private mode: MeasureMode = "off";
  private points: Position[] = [];
  private readonly map: MapLibreMap;
  private readonly onResult: (text: string | null) => void;

  constructor(options: MeasureOptions) {
    this.map = options.map;
    this.onResult = options.onResult;

    this.map.on("click", this.handleClick);
    this.map.on("dblclick", this.handleDoubleClick);
  }

  getMode(): MeasureMode {
    return this.mode;
  }

  setMode(mode: MeasureMode): void {
    this.mode = mode;
    this.points = [];
    this.ensureLayers();
    this.redraw();
    this.map.getCanvas().style.cursor = mode === "off" ? "" : "crosshair";
    // Двойной клик в режиме измерения завершает фигуру, а не приближает карту.
    if (mode === "off") this.map.doubleClickZoom.enable();
    else this.map.doubleClickZoom.disable();
    this.onResult(null);
  }

  clear(): void {
    this.points = [];
    this.redraw();
    this.onResult(null);
  }

  /**
   * Восстановить слои после setStyle.
   *
   * Смена темы пересоздаёт стиль и уносит с собой источник измерений. Без
   * восстановления инструмент молча перестаёт рисовать: клики продолжают
   * копить точки, цифры обновляются, а на карте ничего нет.
   */
  restoreLayers(): void {
    if (this.mode === "off") return;
    this.ensureLayers();
    this.redraw();
  }

  destroy(): void {
    this.map.off("click", this.handleClick);
    this.map.off("dblclick", this.handleDoubleClick);
    this.setMode("off");
    for (const layer of [FILL_LAYER, LINE_LAYER, POINT_LAYER]) {
      if (this.map.getLayer(layer)) this.map.removeLayer(layer);
    }
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID);
  }

  private readonly handleClick = (event: { lngLat: { lng: number; lat: number } }): void => {
    if (this.mode === "off") return;
    this.points.push([event.lngLat.lng, event.lngLat.lat]);
    this.redraw();
  };

  private readonly handleDoubleClick = (): void => {
    if (this.mode === "off") return;
    // Двойной клик добавляет точку дважды — лишнюю убираем, иначе площадь
    // считается по вырожденному ребру нулевой длины.
    if (this.points.length >= 2) this.points.pop();
    this.redraw();
  };

  private ensureLayers(): void {
    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    if (!this.map.getLayer(FILL_LAYER)) {
      this.map.addLayer({
        id: FILL_LAYER,
        type: "fill",
        source: SOURCE_ID,
        filter: ["==", ["geometry-type"], "Polygon"],
        paint: { "fill-color": "#1b6ef3", "fill-opacity": 0.15 },
      });
    }

    if (!this.map.getLayer(LINE_LAYER)) {
      this.map.addLayer({
        id: LINE_LAYER,
        type: "line",
        source: SOURCE_ID,
        filter: ["!=", ["geometry-type"], "Point"],
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#1b6ef3", "line-width": 2.5, "line-dasharray": [2, 1] },
      });
    }

    if (!this.map.getLayer(POINT_LAYER)) {
      this.map.addLayer({
        id: POINT_LAYER,
        type: "circle",
        source: SOURCE_ID,
        filter: ["==", ["geometry-type"], "Point"],
        paint: {
          "circle-radius": 4,
          "circle-color": "#ffffff",
          "circle-stroke-color": "#1b6ef3",
          "circle-stroke-width": 2,
        },
      });
    }
  }

  private redraw(): void {
    const source = this.map.getSource(SOURCE_ID);
    if (!(source instanceof GeoJSONSource)) return;

    const features: Feature[] = this.points.map((position) => ({
      type: "Feature",
      properties: {},
      geometry: { type: "Point", coordinates: position },
    }));

    if (this.mode === "distance" && this.points.length >= 2) {
      const line: Feature<LineString> = {
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: this.points },
      };
      features.push(line);
      this.onResult(`Расстояние: ${formatLength(length(line, { units: "kilometers" }) * 1000)}`);
    } else if (this.mode === "area" && this.points.length >= 3) {
      const ring = [...this.points, this.points[0] as Position];
      const polygon: Feature<Polygon> = {
        type: "Feature",
        properties: {},
        geometry: { type: "Polygon", coordinates: [ring] },
      };
      features.push(polygon);
      const perimeter = length(
        { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: ring } },
        { units: "kilometers" },
      );
      this.onResult(
        `Площадь: ${formatArea(area(polygon))} · периметр: ${formatLength(perimeter * 1000)}`,
      );
    } else if (this.points.length > 0) {
      this.onResult(
        this.mode === "distance"
          ? "Поставьте вторую точку"
          : "Поставьте минимум три точки, двойной клик — завершить",
      );
    } else {
      this.onResult(null);
    }

    // setData в MapLibre 6 возвращает промис. Источник здесь локальный и
    // целиком в памяти — ждать нечего, поэтому промис явно отбрасывается.
    void source.setData({ type: "FeatureCollection", features });
  }
}
